import {
    ArticleOutlined,
    CalendarMonthOutlined,
    CampaignOutlined,
    CategoryOutlined,
    CompareArrowsOutlined,
    DashboardOutlined,
    DescriptionOutlined,
    ExpandLessOutlined,
    ExpandMoreOutlined,
    FactCheckOutlined,
    FolderOutlined,
    FormatListBulletedOutlined,
    GroupsOutlined,
    HandshakeOutlined,
    HelpOutlineOutlined,
    HomeOutlined,
    InfoOutlined,
    Inventory2Outlined,
    LogoutOutlined,
    MenuBookOutlined,
    MenuOutlined,
    PlaylistAddCheckOutlined,
    RocketLaunchOutlined,
    RouteOutlined,
    ScienceOutlined,
    SearchOutlined,
    ShoppingCartOutlined,
    SignalCellularAltOutlined,
    StorefrontOutlined,
    TableChartOutlined,
    TrendingUpOutlined,
    WarehouseOutlined,
} from "@mui/icons-material";
import {
    AppBar,
    Avatar,
    Box,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useCallback, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { useCurrentRoomUser } from "../hooks/useCurrentRoomUser";
import { isStaff, useVisiblePages } from "../hooks/useVisiblePages";
import type { Page, PageTemplate, Room } from "../schema";
import { blackOpacity, chrome } from "../theme/tokens";
import { BisonLogo, SidebarCollapseIcon } from "./BisonLogo";
import { iconSx, labelProps, menuItemSx } from "./nav-styles";
import { RoomSwitcherPanel, SwitchRoomButton } from "./RoomSwitcher";

// Left collapsible drawer — replaces the former two-strip top nav (RoomNav).
// Styled to read pixel-identically to the supplier-portal / content-
// optimization / pricing-universe shell (admin-ui look): 240px expanded / 68px
// collapsed white Drawer, the inline BisonLogo + custom collapse glyph in the
// header, the room's visible pages as the nav group (active = gray surfaceActive
// pill, ink label — no orange), and the signed-in user + sign-out in the footer
// (the portal's profile-chip slot). Since 2026-09-04 consecutive pages that
// share a `navGroup` fold under a collapsible parent node ("About Us",
// "Onboarding"); the parent is a label, not a page.
//
// The supplier logo is NOT here — it lives on the right of each page header
// (BasePage), keeping the supplier's brand front-and-centre while the chrome
// stays Bison-framework-standard.
//
// Props kept identical to the old RoomNav so the routes (Room / PreviewRoom)
// and RoomLayout don't change shape.
export interface RoomNavProps {
    room: Room;
    /** Current page slug, used to highlight the active item. */
    currentPageSlug: string;
    /** URL prefix every link is built from. No trailing slash. */
    basePath: string;
}

const WIDTH_EXPANDED = 240;
const WIDTH_COLLAPSED = 68;
const MOBILE_BAR_HEIGHT = 56;
const STORAGE_KEY = "deal-room.sidebar-collapsed.v1";

export const SIDEBAR_WIDTH_EXPANDED = WIDTH_EXPANDED;
export const SIDEBAR_WIDTH_COLLAPSED = WIDTH_COLLAPSED;
export const SIDEBAR_MOBILE_BAR_HEIGHT = MOBILE_BAR_HEIGHT;

// Per-template nav icon. Pages carry no icon in the schema, so we map by
// template with a neutral default. Icons double as the only affordance in the
// collapsed rail, so every page needs one.
const TEMPLATE_ICONS: Partial<Record<PageTemplate, React.ReactNode>> = {
    welcome: <HomeOutlined />,
    capabilities_overview: <StorefrontOutlined />,
    programs: <CategoryOutlined />,
    supplier_assortment_assessment: <Inventory2Outlined />,
    your_opportunity: <SignalCellularAltOutlined />,
    commercial_proposal: <FormatListBulletedOutlined />,
    partnership_form: <FormatListBulletedOutlined />,
    contracts: <DescriptionOutlined />,
    map: <PlaylistAddCheckOutlined />,
    marketing: <CampaignOutlined />,
    onboarding: <RocketLaunchOutlined />,
    uploads: <FolderOutlined />,
    case_studies: <MenuBookOutlined />,
    supplier_portal_preview: <DashboardOutlined />,
    faq: <HelpOutlineOutlined />,
    how_we_partner: <HandshakeOutlined />,
    // Content Optimization room (2026-09-23).
    co_proposal: <SignalCellularAltOutlined />,
    co_assortment: <TableChartOutlined />,
    co_capabilities: <StorefrontOutlined />,
    co_checkout: <ShoppingCartOutlined />,
};

// Icons for the proposal page's in-page sections (canonical ids).
const SECTION_ICONS: Record<string, React.ReactNode> = {
    findings: <SearchOutlined />,
    "shopper-journey": <RouteOutlined />,
    opportunity: <SignalCellularAltOutlined />,
    "example-product": <CompareArrowsOutlined />,
};

// A page may name its own icon in `data.navIcon` (pitch rooms built from
// specialty pages would otherwise all share the article glyph; Tvilum,
// 2026-09-24). The key wins over the template default.
const NAV_ICONS: Record<string, React.ReactNode> = {
    home: <HomeOutlined />,
    handshake: <HandshakeOutlined />,
    inventory: <Inventory2Outlined />,
    warehouse: <WarehouseOutlined />,
    storefront: <StorefrontOutlined />,
    science: <ScienceOutlined />,
    "fact-check": <FactCheckOutlined />,
    description: <DescriptionOutlined />,
    help: <HelpOutlineOutlined />,
    "trending-up": <TrendingUpOutlined />,
    people: <GroupsOutlined />,
    calendar: <CalendarMonthOutlined />,
    metrics: <SignalCellularAltOutlined />,
};

function iconFor(page: Page): React.ReactNode {
    const key = page.data?.navIcon;
    if (typeof key === "string" && NAV_ICONS[key]) return NAV_ICONS[key];
    return TEMPLATE_ICONS[page.template] ?? <ArticleOutlined />;
}

// Parent-node icons, keyed by the page's `navGroup` (canonical sets it per
// template: About Us = How We Partner / Capabilities / Programs / FAQ, Onboarding = Partnership
// Details / Agreements / Onboarding / Checklist / Documents). Unknown groups
// get a folder. Parent labels carry the same weight as page labels.
const GROUP_ICONS: Record<string, React.ReactNode> = {
    "About Us": <InfoOutlined />,
    Onboarding: <RocketLaunchOutlined />,
};

// The nav is the visible page list folded into entries: a bare page, or a
// parent node holding the run of consecutive pages that share a `navGroup`.
// `index` is the page's position in the flat visible list — the first visible
// page is the room home and links to the bare basePath.
interface NavPage {
    page: Page;
    index: number;
}
type NavEntry = { kind: "page"; item: NavPage } | { kind: "group"; name: string; items: NavPage[] };

function buildNavEntries(pages: Page[]): NavEntry[] {
    const entries: NavEntry[] = [];
    pages.forEach((page, index) => {
        const last = entries[entries.length - 1];
        if (page.navGroup) {
            if (last && last.kind === "group" && last.name === page.navGroup) {
                last.items.push({ page, index });
            } else {
                entries.push({ kind: "group", name: page.navGroup, items: [{ page, index }] });
            }
        } else {
            entries.push({ kind: "page", item: { page, index } });
        }
    });
    return entries;
}

// Shared nav-item styling — mirrors content-optimization-web's `menuItemSx`:
// gray active pill, ink active text / inkBody idle text, 13px, no underline.
export function RoomSidebar({ room, currentPageSlug, basePath }: RoomNavProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const { pages } = useVisiblePages(room);

    // Lazy initializer reads the persisted collapse state once, at mount —
    // avoids a setState-in-effect cascade (this is a Vite SPA, window is safe).
    const [isExpanded, setIsExpanded] = useState(() => {
        try {
            return window.localStorage.getItem(STORAGE_KEY) !== "1";
        } catch {
            return true; // private mode — keep default expanded
        }
    });
    const [mobileOpen, setMobileOpen] = useState(false);

    const toggleCollapse = useCallback(() => {
        setIsExpanded((prev) => {
            const next = !prev;
            try {
                window.localStorage.setItem(STORAGE_KEY, next ? "0" : "1");
            } catch {
                /* skip */
            }
            return next;
        });
    }, []);

    // On mobile the drawer is always full-width content (no collapsed rail).
    const expanded = isMobile ? true : isExpanded;

    const content = (
        <DrawerContent
            room={room}
            pages={pages}
            currentPageSlug={currentPageSlug}
            basePath={basePath}
            expanded={expanded}
            isMobile={isMobile}
            onToggleCollapse={toggleCollapse}
            onNavigate={() => setMobileOpen(false)}
        />
    );

    if (isMobile) {
        return (
            <>
                <AppBar
                    position="fixed"
                    elevation={0}
                    sx={{
                        bgcolor: "primary.main",
                        height: MOBILE_BAR_HEIGHT,
                        justifyContent: "center",
                    }}
                >
                    <Toolbar
                        variant="dense"
                        sx={{
                            minHeight: MOBILE_BAR_HEIGHT,
                            gap: 1,
                            // Gutters match the page body's 24px so the hamburger and
                            // logo line up with the content (Elijah, 2026-11-01).
                            px: 3,
                        }}
                    >
                        {/* Hamburger top-left, beside the edge the drawer slides in
                            from; the brandmark sits top-right (Elijah, 2026-10-31). */}
                        <IconButton
                            edge="start"
                            onClick={() => setMobileOpen(true)}
                            aria-label="Open navigation"
                            sx={{
                                color: "common.white",
                                // 20% larger glyph; the margin cancels the button's 8px
                                // padding and the glyph's own 3.6px inset so the bars
                                // start on the body's left edge.
                                ml: "-11.6px",
                                "& svg": { fontSize: 28.8 },
                            }}
                        >
                            <MenuOutlined />
                        </IconButton>
                        <Box sx={{ flexGrow: 1 }} />
                        {/* The brandmark is the way home on a phone too: it links to
                            the bare basePath (the Welcome page), like the drawer's
                            logo on desktop (Bill, 2026-09-08). */}
                        <Box
                            component={RouterLink}
                            to={basePath}
                            aria-label="Bison Commerce"
                            sx={{ display: "inline-flex", alignItems: "center" }}
                        >
                            <Box
                                component="img"
                                src="/brand/bison-logo-dark.svg"
                                alt="Bison Commerce"
                                sx={{ height: 26.4, display: "block" }} // 24 + 10% (Bill, 2026-09-09)
                            />
                        </Box>
                    </Toolbar>
                </AppBar>
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        "& .MuiDrawer-paper": {
                            width: WIDTH_EXPANDED,
                            boxSizing: "border-box",
                            bgcolor: "#FFFFFF",
                        },
                    }}
                >
                    {content}
                </Drawer>
            </>
        );
    }

    const width = expanded ? WIDTH_EXPANDED : WIDTH_COLLAPSED;
    return (
        <Drawer
            component="nav"
            variant="permanent"
            sx={{
                width,
                flexShrink: 0,
                "& .MuiDrawer-paper": {
                    width,
                    boxSizing: "border-box",
                    borderRight: `1px solid ${chrome.borderPrimary}`,
                    bgcolor: "#FFFFFF",
                    overflowX: "hidden",
                    transition: "width 0.2s ease",
                },
            }}
        >
            {content}
        </Drawer>
    );
}

function DrawerContent({
    room,
    pages,
    currentPageSlug,
    basePath,
    expanded,
    isMobile,
    onToggleCollapse,
    onNavigate,
}: {
    room: Room;
    pages: Page[];
    currentPageSlug: string;
    basePath: string;
    expanded: boolean;
    isMobile: boolean;
    onToggleCollapse: () => void;
    onNavigate: () => void;
}) {
    // The first visible page is the room home — its link and the Bison
    // brandmark both point at the bare basePath (cleaner URL); deep links to
    // /{basePath}/{slug} still resolve via the [page] route.
    const hrefFor = (slug: string, isFirst: boolean) =>
        isFirst ? basePath : `${basePath}/${slug}`;

    const entries = buildNavEntries(pages);
    const { hash } = useLocation();

    // Staff room switcher: swaps the page list for a search panel in place.
    // From the collapsed rail it opens the drawer first; leaving the room
    // (any navigation) closes it.
    const [switching, setSwitching] = useState(false);
    const toggleSwitching = () => {
        if (!switching && !expanded) onToggleCollapse();
        setSwitching((prev) => !prev);
    };

    // Parent nodes start open; a click on the parent folds its children. In
    // the collapsed rail there is no room for a parent row, so a group's
    // pages render flat with tooltips like every other page.
    const [closedGroups, setClosedGroups] = useState<Record<string, boolean>>({});
    const toggleGroup = (name: string) =>
        setClosedGroups((prev) => ({ ...prev, [name]: !prev[name] }));

    // In-page anchors (Content Optimization proposal, 2026-09-23, Eric): a
    // page whose `data.sections` lists `{ id, label }` shows those sections
    // as its nav rows, flat at page level and in the page's place (no row for
    // the page itself, no hierarchy); each is a hash link, the reader can
    // click through or just scroll. The active row follows the URL hash, and
    // the first section is active when the page is open with no hash.
    const sections = (page: Page): { id: string; label: string }[] => {
        const list = (page.data as { sections?: unknown } | undefined)?.sections;
        return Array.isArray(list)
            ? list.filter(
                  (x): x is { id: string; label: string } =>
                      typeof (x as { id?: unknown }).id === "string" &&
                      typeof (x as { label?: unknown }).label === "string",
              )
            : [];
    };

    const renderPage = ({ page, index }: NavPage, indent: boolean) => {
        const active = page.slug === currentPageSlug;
        const anchors = sections(page);
        if (anchors.length) {
            const href = hrefFor(page.slug, index === 0);
            return anchors.map((a, i) => {
                const on = active && (hash ? hash === `#${a.id}` : i === 0);
                const row = (
                    <ListItem key={`${page.slug}#${a.id}`} disablePadding>
                        <MenuItem
                            component={RouterLink}
                            to={`${href}#${a.id}`}
                            onClick={onNavigate}
                            selected={on}
                            sx={{
                                ...menuItemSx(on, expanded),
                                ...(indent && expanded ? { pl: 4.5 } : {}),
                            }}
                        >
                            <ListItemIcon
                                sx={{ ...iconSx, color: on ? chrome.ink : chrome.iconPrimary }}
                            >
                                {SECTION_ICONS[a.id] ?? iconFor(page)}
                            </ListItemIcon>
                            {expanded && (
                                <ListItemText
                                    primary={a.label}
                                    primaryTypographyProps={labelProps(on)}
                                />
                            )}
                        </MenuItem>
                    </ListItem>
                );
                return expanded ? (
                    row
                ) : (
                    <Tooltip key={`${page.slug}#${a.id}`} title={a.label} placement="right">
                        {row}
                    </Tooltip>
                );
            });
        }
        const item = (
            <ListItem key={page.slug} disablePadding>
                <MenuItem
                    component={RouterLink}
                    to={hrefFor(page.slug, index === 0)}
                    onClick={onNavigate}
                    selected={active}
                    sx={{
                        ...menuItemSx(active, expanded),
                        ...(indent && expanded ? { pl: 4.5 } : {}),
                    }}
                >
                    <ListItemIcon
                        sx={{ ...iconSx, color: active ? chrome.ink : chrome.iconPrimary }}
                    >
                        {iconFor(page)}
                    </ListItemIcon>
                    {expanded && (
                        <ListItemText
                            primary={page.navLabel ?? page.title}
                            primaryTypographyProps={labelProps(active)}
                        />
                    )}
                </MenuItem>
            </ListItem>
        );
        return expanded ? (
            item
        ) : (
            <Tooltip key={page.slug} title={page.navLabel ?? page.title} placement="right">
                {item}
            </Tooltip>
        );
    };

    const renderGroup = (name: string, items: NavPage[]) => {
        if (!expanded) return items.map((item) => renderPage(item, false));
        const open = !closedGroups[name];
        const childActive = items.some(({ page }) => page.slug === currentPageSlug);
        return (
            <Box key={`group-${name}`}>
                <ListItem disablePadding>
                    <MenuItem
                        component="div"
                        role="button"
                        onClick={() => toggleGroup(name)}
                        aria-expanded={open}
                        sx={menuItemSx(false, expanded)}
                    >
                        <ListItemIcon
                            sx={{ ...iconSx, color: childActive ? chrome.ink : chrome.iconPrimary }}
                        >
                            {GROUP_ICONS[name] ?? <FolderOutlined />}
                        </ListItemIcon>
                        <ListItemText
                            primary={name}
                            primaryTypographyProps={labelProps(childActive)}
                        />
                        {open ? (
                            <ExpandLessOutlined sx={{ fontSize: 18, color: chrome.iconPrimary }} />
                        ) : (
                            <ExpandMoreOutlined sx={{ fontSize: 18, color: chrome.iconPrimary }} />
                        )}
                    </MenuItem>
                </ListItem>
                {open && items.map((item) => renderPage(item, true))}
            </Box>
        );
    };

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", px: 2 }}>
            {/* Header — Bison logo + collapse trigger */}
            <Box
                sx={{
                    height: 70,
                    minHeight: 70,
                    display: "flex",
                    alignItems: "center",
                    borderBottom: `1px solid ${chrome.borderPrimary}`,
                    justifyContent: expanded ? "space-between" : "center",
                }}
            >
                {expanded && (
                    <Box
                        component={RouterLink}
                        to={basePath}
                        onClick={onNavigate}
                        aria-label="Bison Commerce"
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            textDecoration: "none",
                        }}
                    >
                        <BisonLogo width={132} />
                    </Box>
                )}
                {!isMobile && (
                    <IconButton
                        size="small"
                        onClick={onToggleCollapse}
                        aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
                        sx={{ color: chrome.iconPrimary }}
                    >
                        <SidebarCollapseIcon flipped={!expanded} />
                    </IconButton>
                )}
            </Box>

            {/* Page nav, or the room switcher in its place */}
            {switching && expanded ? (
                <RoomSwitcherPanel
                    room={room}
                    currentPage={(() => {
                        const i = pages.findIndex((p) => p.slug === currentPageSlug);
                        return i < 0 ? undefined : { slug: pages[i].slug, template: pages[i].template, isHome: i === 0 };
                    })()}
                    onClose={() => setSwitching(false)}
                    onNavigate={onNavigate}
                />
            ) : (
                <List
                    component="div"
                    sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", mx: -1.5, px: 1.5, py: 1 }}
                >
                    {entries.map((entry) =>
                        entry.kind === "page"
                            ? renderPage(entry.item, false)
                            : renderGroup(entry.name, entry.items),
                    )}
                </List>
            )}

            {/* Footer — signed-in user + sign out (portal's profile-chip slot) */}
            <SidebarFooter
                room={room}
                expanded={expanded}
                switching={switching && expanded}
                onSwitch={toggleSwitching}
            />
        </Box>
    );
}

// Auth footer — ported from the former top UserBadge strip, styled to match the
// portal's footer profile chip. Renders only when a session cookie resolves to
// a user, so it stays invisible on the open preview path exactly as before.
function SidebarFooter({
    room,
    expanded,
    switching,
    onSwitch,
}: {
    room: Room;
    expanded: boolean;
    switching: boolean;
    onSwitch: () => void;
}) {
    const { user, loading } = useCurrentRoomUser();
    if (loading || !user) return null;

    const initial = (user.name ?? user.email).slice(0, 1).toUpperCase();

    return (
        <Box sx={{ py: 1 }}>
            <Divider sx={{ borderColor: chrome.borderPrimary, mb: 1 }} />
            {isStaff(user.email) && (
                <SwitchRoomButton room={room} expanded={expanded} active={switching} onClick={onSwitch} />
            )}
            <Box
                sx={{
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    justifyContent: expanded ? "flex-start" : "center",
                }}
            >
                <Tooltip title={expanded ? "" : `Signed in as ${user.email}`} placement="right">
                    <Avatar
                        sx={{
                            width: 20,
                            height: 20,
                            fontSize: 12,
                            fontWeight: 600,
                            borderRadius: "4px",
                            bgcolor: blackOpacity[80],
                            color: "#FFFFFF",
                        }}
                    >
                        {initial}
                    </Avatar>
                </Tooltip>
                {expanded && (
                    <>
                        <Typography
                            title={user.email}
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                fontWeight: 500,
                                fontSize: 13,
                                lineHeight: "20px",
                                color: chrome.ink,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {user.email}
                        </Typography>
                        <Box
                            component="form"
                            method="POST"
                            action="/api/auth/logout"
                            sx={{ display: "inline-flex" }}
                        >
                            <Tooltip title="Sign out" placement="top">
                                <IconButton
                                    type="submit"
                                    size="small"
                                    aria-label="Sign out"
                                    sx={{ color: chrome.iconPrimary }}
                                >
                                    <LogoutOutlined sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
}
